import {
  ExtranetCustomerGroupPricingContractDto,
  ExtranetCustomerGroupScheduleSlotDto,
} from '@pharmac-extranet/extranet-api'
import {
  ControlledCheckbox,
  ControlledSelect,
  ExtranetContainer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacButton } from '@pharmac/ui'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { api } from '../../services/HttpService'
import {
  ExtranetFiltersColumn,
  ExtranetFiltersFooter,
  ExtranetFiltersRow,
} from '../filters/ExtranetFiltersComponents'
import { isWeekdaySunday, weekdayShortTranslations } from './util/date-utils'
import {
  GroupSettingFilters,
  mapCustomerGroupSearchParamsToQueryParams,
} from './util/query-parameter-mapping'

type Props = {
  closeFilters: () => void
  customerGroupId: string
}

export const CustomerGroupsFilters = ({ closeFilters, customerGroupId }: Props) => {
  const { t } = useLanguage()
  const { minDesktop } = useBreakpoint()
  const { customer } = useCustomerContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState(mapCustomerGroupSearchParamsToQueryParams(searchParams))

  const getCustomerGroupSchedule = (
    customerId?: number
  ): UseQueryOptions<Array<ExtranetCustomerGroupScheduleSlotDto>> => ({
    queryFn: async () => await api.customers.getCustomerGroupScheduleSlots(customerId as number),
    queryKey: ['__customerGroupSchedule', customerId],
    enabled: typeof customerId === 'number',
  })
  const { data: customerGroupSchedule } = useQuery(getCustomerGroupSchedule(customer?.id))

  const getCustomerGroupPricing = (
    customerId?: number,
    groupId?: string
  ): UseQueryOptions<Array<ExtranetCustomerGroupPricingContractDto>> => ({
    queryFn: async () =>
      await api.customers.getCustomerGroupPricing(customerId as number, groupId as string),
    queryKey: ['__customerGroupPricing', customer?.id, customerGroupId],
    enabled: typeof customerId === 'number' && typeof groupId === 'string',
  })
  const { data: customerGroupPricing } = useQuery(
    getCustomerGroupPricing(customer?.id, customerGroupId)
  )

  const formhook = useForm<GroupSettingFilters>({
    mode: 'onChange',
    defaultValues: {
      active: filters.active ?? undefined,
      passive: filters.passive ?? undefined,
      productId: filters.productId ?? '',
      scheduleSlotId: filters.scheduleSlotId ?? '',
      productNotSet: filters.productNotSet ?? undefined,
      scheduleSlotNotSet: filters.scheduleSlotNotSet ?? undefined,
    },
  })

  useEffect(() => {
    const params = mapCustomerGroupSearchParamsToQueryParams(searchParams)

    setFilters(params)

    formhook.reset({
      active: params.active ?? undefined,
      passive: params.passive ?? undefined,
      productId: params.productId ?? '',
      productNotSet: params.productNotSet ?? undefined,
      scheduleSlotId: params.scheduleSlotId ?? '',
      scheduleSlotNotSet: params.scheduleSlotNotSet ?? undefined,
    })
  }, [searchParams])

  const handleSubmit = (values: GroupSettingFilters) => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...filters,
          ...values,
        })
      )
    )
    closeFilters()
  }

  return (
    <>
      <FormProvider {...formhook}>
        <ExtranetFiltersRow>
          <ExtranetContainer
            flexRow={minDesktop}
            flexColumn={!minDesktop}
            full
          >
            <ExtranetContainer full>
              <ExtranetFiltersColumn style={{ width: '100%' }}>
                <div style={{ width: '80%' }}>
                  <ControlledSelect
                    label={t('extranet:customerGroupSettings.doseDispensingFee')}
                    control={formhook.control as unknown as Control<FieldValues>}
                    name="productId"
                  >
                    <option value="">-</option>
                    {customerGroupPricing?.map((a) => (
                      <option
                        key={a.contractId + a.productName}
                        value={a.pricingProductId}
                      >
                        {a.productName}
                      </option>
                    ))}
                  </ControlledSelect>
                </div>

                <div
                  style={
                    minDesktop
                      ? { height: 'var(--spacing-large)' }
                      : { height: 'var(--spacing-medium)' }
                  }
                />
                <ControlledCheckbox
                  checkboxLabel={t('extranet:customerGroupSettings.doseDispensingFeeNotSet')}
                  name="productNotSet"
                  control={formhook.control as unknown as Control<FieldValues>}
                />
              </ExtranetFiltersColumn>
            </ExtranetContainer>
            <ExtranetContainer full>
              <ExtranetFiltersColumn style={{ width: '100%' }}>
                <div style={{ width: '80%' }}>
                  <ControlledSelect
                    label={t('extranet:customerGroupSettings.orderTime')}
                    control={formhook.control as unknown as Control<FieldValues>}
                    name="scheduleSlotId"
                  >
                    <option value="">-</option>
                    {customerGroupSchedule?.map((a) => (
                      <option
                        key={a.id}
                        value={a.id}
                      >
                        <>
                          {t('common:week')} {isWeekdaySunday(a.cycleDateIndex) ? 1 : 2},{' '}
                          {t(weekdayShortTranslations(a.deadlineWeekday) as any)} {a.deadlineTime}
                        </>
                      </option>
                    ))}
                  </ControlledSelect>
                </div>
                <div
                  style={
                    minDesktop
                      ? { height: 'var(--spacing-large)' }
                      : { height: 'var(--spacing-medium)' }
                  }
                />
                <ControlledCheckbox
                  checkboxLabel={t('extranet:customerGroupSettings.orderTimeNotSet')}
                  name="scheduleSlotNotSet"
                  control={formhook.control as unknown as Control<FieldValues>}
                />
              </ExtranetFiltersColumn>
            </ExtranetContainer>
            <ExtranetContainer full>
              <ExtranetFiltersColumn style={{ width: '100%' }}>
                <ControlledCheckbox
                  label="Aktiivisuus"
                  checkboxLabel={t('common:active')}
                  name="active"
                  control={formhook.control as unknown as Control<FieldValues>}
                />
                <div style={{ height: 'var(--spacing-small)' }} />
                <ControlledCheckbox
                  checkboxLabel={t('common:passive')}
                  name="passive"
                  control={formhook.control as unknown as Control<FieldValues>}
                />
              </ExtranetFiltersColumn>
            </ExtranetContainer>
          </ExtranetContainer>
        </ExtranetFiltersRow>
        <ExtranetFiltersFooter>
          <PharmacButton
            onClick={formhook.handleSubmit(handleSubmit)}
            disabled={!formhook.formState.isDirty}
          >
            {t('common:search')}
          </PharmacButton>
        </ExtranetFiltersFooter>
      </FormProvider>
    </>
  )
}
